// ============================================================================
// THE CYCLE — Fantasy Value Board (FVS)
// CVR-first fantasy analytics: 30-day rolling form × salary efficiency × trend
// ============================================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, Typography, Avatar, Chip, Skeleton,
  useTheme, alpha, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  IconButton, Tabs, Tab,
} from '@mui/material';
import {
  Casino, Whatshot, AcUnit, TrendingFlat,
  Search, AttachMoney, EmojiEvents, Star, BookmarkAdd, BookmarkRemove,
  SportsBaseball, Bolt,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { playersApi, gamesApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { makePlayerSlug } from '../utils/playerSlug';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';

// ─── constants ───────────────────────────────────────────────────────────────

const ROSTER_KEY = 'tc-fantasy-roster';
const MLB_TEAMS = [
  'ARI','ATL','BAL','BOS','CHC','CWS','CIN','CLE','COL','DET',
  'HOU','KC','LAA','LAD','MIA','MIL','MIN','NYM','NYY','OAK',
  'PHI','PIT','SD','SEA','SF','STL','TB','TEX','TOR','WSH',
];
const TH = { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 };

// ─── helpers ─────────────────────────────────────────────────────────────────

const loadRoster  = () => { try { return JSON.parse(localStorage.getItem(ROSTER_KEY) || '[]'); } catch { return []; } };
const saveRoster  = (r) => { try { localStorage.setItem(ROSTER_KEY, JSON.stringify(r)); } catch { /* noop */ } };

const cvrColor = (cvr) => {
  if (cvr >= 80) return '#16a34a';
  if (cvr >= 65) return '#0891b2';
  if (cvr >= 50) return '#ca8a04';
  if (cvr >= 35) return '#ea580c';
  return '#dc2626';
};

const fmtSal = (s) => {
  if (!s) return '—';
  if (s >= 1_000_000) return `$${(s / 1_000_000).toFixed(1)}M`;
  return `$${Math.round(s / 1000)}K`;
};

// Fantasy Value Score = CVR quality + salary efficiency + trend + tonight bonus
const calcFVS = (cvr, salary, acvrTrend, playsToday) => {
  const cvrPts = (cvr || 0) * 0.60;                     // 0-60

  const sal = salary?.salary;
  let salPts = 0;
  if (!sal || sal <= 700_000)   salPts = 30;
  else if (sal <= 3_000_000)    salPts = 26;
  else if (sal <= 7_000_000)    salPts = 20;
  else if (sal <= 15_000_000)   salPts = 12;
  else if (sal <= 30_000_000)   salPts = 5;

  const trendPts = acvrTrend === 'up' ? 10 : acvrTrend === 'down' ? -5 : 0;
  const todayPts = playsToday ? 15 : 0;

  return Math.round(cvrPts + salPts * 0.30 + trendPts * 0.10 + todayPts);
};

const getValueBadge = (salary, cvr) => {
  const sal = salary?.salary;
  if (!sal || sal <= 700_000)              return { label: 'PRE-ARB STEAL', color: '#16a34a' };
  if (sal <= 5_000_000 && cvr >= 65)       return { label: 'VALUE PICK',    color: '#0891b2' };
  if (sal <= 12_000_000 && cvr >= 60)      return { label: 'UNDERVALUED',   color: '#ca8a04' };
  if (cvr >= 80)                           return { label: 'STAR',           color: '#7c3aed' };
  return null;
};

// ─── TrendIcon ────────────────────────────────────────────────────────────────

const TrendIcon = ({ trend }) => {
  if (trend === 'up')   return <Whatshot   sx={{ fontSize: 14, color: '#f59e0b' }} />;
  if (trend === 'down') return <AcUnit     sx={{ fontSize: 14, color: '#60a5fa' }} />;
  return                       <TrendingFlat sx={{ fontSize: 14, color: '#9ca3af' }} />;
};

// ─── PlayerRow ────────────────────────────────────────────────────────────────

const PlayerRow = ({ rank, player: p, isPitcher, showFVS, inRoster, onToggleRoster, onNavigate, theme }) => {
  const cvr   = p.cvr || 0;
  const color = cvrColor(cvr);
  const bat   = p.stats?.batting  || {};
  const pit   = p.stats?.pitching || {};

  const ops = bat.ops              != null ? bat.ops.toFixed(3)              : '—';
  const hr  = bat.homeRuns         != null ? String(bat.homeRuns)            : '—';
  const era = pit.era              != null ? pit.era.toFixed(2)              : '—';
  const k9  = pit.strikeoutsPer9   != null ? pit.strikeoutsPer9.toFixed(1)  : '—';

  const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
  const rankBg     = rank <= 3 ? rankColors[rank - 1] : 'transparent';

  return (
    <TableRow
      component={motion.tr}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: Math.min(rank * 0.012, 0.35) }}
      hover
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        ...(p.playsToday && { bgcolor: alpha('#f59e0b', 0.03) }),
      }}
      onClick={onNavigate}
    >
      {/* Rank */}
      <TableCell sx={{ width: 36, pl: 1.5 }}>
        <Box sx={{
          width: 22, height: 22, borderRadius: '50%',
          bgcolor: rankBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.72rem', fontWeight: 700,
          color: rank <= 3 ? '#000' : 'text.secondary',
        }}>
          {rank}
        </Box>
      </TableCell>

      {/* Player info */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={getTeamLogoUrl(p.team)} sx={{ width: 22, height: 22 }} variant="rounded" />
          <Box>
            <Typography variant="body2" fontWeight={600} lineHeight={1.1} noWrap>{p.name}</Typography>
            <Typography variant="caption" color="text.secondary">{p.team} · {p.position || '—'}</Typography>
          </Box>
          {p.badge && (
            <Chip label={p.badge.label} size="small" sx={{
              height: 16, fontSize: '0.6rem', fontWeight: 700,
              bgcolor: alpha(p.badge.color, 0.12), color: p.badge.color,
            }} />
          )}
        </Box>
      </TableCell>

      {/* FVS */}
      {showFVS && (
        <TableCell align="center">
          <Typography variant="body2" fontWeight={700} sx={{ color: p.fvs >= 65 ? '#7c3aed' : 'text.primary' }}>
            {p.fvs}
          </Typography>
        </TableCell>
      )}

      {/* CVR */}
      <TableCell align="center" onClick={e => e.stopPropagation()}>
        <Chip label={`CVR ${cvr}`} size="small" sx={{
          height: 20, fontSize: '0.72rem', fontWeight: 700,
          bgcolor: alpha(color, 0.12), color,
        }} />
      </TableCell>

      {/* Trend */}
      <TableCell align="center"><TrendIcon trend={p.acvrTrend} /></TableCell>

      {/* Salary */}
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {fmtSal(p.salary?.salary)}
        </Typography>
      </TableCell>

      {/* OPS or ERA */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {isPitcher ? era : ops}
        </Typography>
      </TableCell>

      {/* HR or K/9 */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {isPitcher ? k9 : hr}
        </Typography>
      </TableCell>

      {/* Tonight indicator */}
      <TableCell align="center">
        {p.playsToday
          ? <Bolt sx={{ fontSize: 16, color: '#f59e0b' }} />
          : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>

      {/* Roster bookmark */}
      <TableCell align="center" onClick={e => { e.stopPropagation(); onToggleRoster(); }}>
        <IconButton size="small">
          {inRoster
            ? <BookmarkRemove sx={{ fontSize: 16, color: '#7c3aed' }} />
            : <BookmarkAdd    sx={{ fontSize: 16, color: 'text.disabled' }} />}
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

// ─── PlayerTable ──────────────────────────────────────────────────────────────

const PlayerTable = ({ players, loading, isPitcher, showFVS, isInRoster, toggleRoster, navigate, theme }) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
            <TableCell sx={{ ...TH, width: 36 }}>#</TableCell>
            <TableCell sx={TH}>Player</TableCell>
            {showFVS && <TableCell sx={{ ...TH, textAlign: 'center' }}>FVS</TableCell>}
            <TableCell sx={{ ...TH, textAlign: 'center' }}>CVR</TableCell>
            <TableCell sx={{ ...TH, textAlign: 'center' }}>Trend</TableCell>
            <TableCell sx={{ ...TH, textAlign: 'right' }}>Salary</TableCell>
            <TableCell sx={{ ...TH, textAlign: 'center' }}>{isPitcher ? 'ERA' : 'OPS'}</TableCell>
            <TableCell sx={{ ...TH, textAlign: 'center' }}>{isPitcher ? 'K/9' : 'HR'}</TableCell>
            <TableCell sx={{ ...TH, textAlign: 'center' }}>Tonight</TableCell>
            <TableCell sx={{ ...TH, textAlign: 'center', width: 40 }}>★</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array(8).fill(0).map((_, i) => (
                <TableRow key={i}>
                  {Array(showFVS ? 10 : 9).fill(0).map((__, j) => (
                    <TableCell key={j}><Skeleton width={j === 1 ? 130 : 40} /></TableCell>
                  ))}
                </TableRow>
              ))
            : players.length === 0
            ? (
                <TableRow>
                  <TableCell colSpan={showFVS ? 10 : 9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No players match your filters
                  </TableCell>
                </TableRow>
              )
            : players.map((p, i) => (
                <PlayerRow
                  key={p.id} rank={i + 1} player={p}
                  isPitcher={isPitcher} showFVS={showFVS}
                  inRoster={isInRoster(p.id)}
                  onToggleRoster={() => toggleRoster(p.id)}
                  onNavigate={() => navigate(`/players/profile/${makePlayerSlug(p.name)}`)}
                  theme={theme}
                />
              ))
          }
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

// ─── main page ────────────────────────────────────────────────────────────────

const FantasyDFS = () => {
  const theme    = useTheme();
  const navigate = useNavigate();
  const { year } = useYear();

  const [tab, setTab]                 = useState(0);
  const [view, setView]               = useState('batters');
  const [loading, setLoading]         = useState(true);
  const [batters, setBatters]         = useState([]);
  const [pitchers, setPitchers]       = useState([]);
  const [playingToday, setPlayingToday] = useState(new Set());
  const [search, setSearch]           = useState('');
  const [posFilter, setPosFilter]     = useState('all');
  const [teamFilter, setTeamFilter]   = useState('');
  const [roster, setRoster]           = useState(loadRoster);

  const isInRoster   = useCallback((id) => roster.includes(id), [roster]);
  const toggleRoster = useCallback((id) => {
    setRoster(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      saveRoster(next);
      return next;
    });
  }, []);

  // ── fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const today = new Date().toISOString().slice(0, 10);

    Promise.all([
      playersApi.getPlayers({ year, mode: 'rolling', playerType: 'Batter',  sortBy: 'cvr', sortOrder: 'desc', limit: 400, minGames: 5 }).catch(() => ({ players: [] })),
      playersApi.getPlayers({ year, mode: 'rolling', playerType: 'Pitcher', sortBy: 'cvr', sortOrder: 'desc', limit: 250, minGames: 5 }).catch(() => ({ players: [] })),
      gamesApi.getLiveSchedule(today).catch(() => ({ games: [] })),
    ]).then(([batRes, pitRes, sched]) => {
      if (!alive) return;
      const teams = new Set();
      (sched?.games || []).forEach(g => {
        if (g.homeTeam?.abbreviation) teams.add(g.homeTeam.abbreviation);
        if (g.awayTeam?.abbreviation) teams.add(g.awayTeam.abbreviation);
      });
      setPlayingToday(teams);
      setBatters(batRes?.players || []);
      setPitchers(pitRes?.players || []);
      setLoading(false);
    });

    return () => { alive = false; };
  }, [year]);

  // ── enrich with FVS ────────────────────────────────────────────────────────
  const enrichSource = useCallback((source) =>
    source.map(p => ({
      ...p,
      playsToday: playingToday.has(p.team),
      fvs: calcFVS(p.cvr, p.salary, p.acvrTrend, playingToday.has(p.team)),
      badge: getValueBadge(p.salary, p.cvr),
    })),
    [playingToday]
  );

  const allBatters  = useMemo(() => enrichSource(batters),  [enrichSource, batters]);
  const allPitchers = useMemo(() => enrichSource(pitchers), [enrichSource, pitchers]);

  // ── filter ─────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = view === 'batters' ? allBatters : allPitchers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.team.toLowerCase().includes(q));
    }
    if (posFilter !== 'all') {
      list = list.filter(p => {
        const pos = (p.position || '').toUpperCase();
        if (posFilter === 'OF') return ['LF','CF','RF','OF'].includes(pos);
        return pos === posFilter;
      });
    }
    if (teamFilter) list = list.filter(p => p.team === teamFilter);
    return list;
  }, [view, allBatters, allPitchers, search, posFilter, teamFilter]);

  // ── tab subsets ────────────────────────────────────────────────────────────
  const tonight    = useMemo(() => [...filtered].filter(p => p.playsToday).sort((a, b) => b.fvs - a.fvs), [filtered]);
  const valueBoard = useMemo(() => {
    const under12M = p => !p.salary?.salary || p.salary.salary <= 12_000_000;
    return filtered.filter(p => p.cvr >= 55 && under12M(p)).sort((a, b) => b.fvs - a.fvs);
  }, [filtered]);
  const fullBoard  = useMemo(() => [...filtered].sort((a, b) => b.cvr - a.cvr), [filtered]);
  const myRoster   = useMemo(() => {
    const all = [...allBatters, ...allPitchers];
    return roster.map(id => all.find(p => p.id === id)).filter(Boolean);
  }, [roster, allBatters, allPitchers]);

  const activeList = [tonight, valueBoard, fullBoard, myRoster][tab] ?? fullBoard;
  const showFVS    = tab !== 2 && tab !== 3; // FVS column on Tonight + Value tabs
  const isPitcher  = view === 'pitchers';

  const positions  = isPitcher
    ? ['all', 'SP', 'RP']
    : ['all', 'C', '1B', '2B', '3B', 'SS', 'OF', 'DH'];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>

      {/* ── Header ── */}
      <Box sx={{
        mb: 3, p: 2.5, borderRadius: 3,
        background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
        color: 'white', display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Casino sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} lineHeight={1.1}>Fantasy Value Board</Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.25 }}>
            CVR-powered rankings — 30-day rolling form × salary efficiency × tonight's slate
          </Typography>
        </Box>
        <PageYearSelector />
      </Box>

      {/* ── Controls ── */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <ToggleButtonGroup value={view} exclusive size="small"
          onChange={(_, v) => { if (v) { setView(v); setPosFilter('all'); } }}>
          <ToggleButton value="batters"  sx={{ px: 2, fontWeight: 700 }}>Batters</ToggleButton>
          <ToggleButton value="pitchers" sx={{ px: 2, fontWeight: 700 }}>Pitchers</ToggleButton>
        </ToggleButtonGroup>

        <TextField size="small" placeholder="Search player…" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} /> }}
          sx={{ width: 190 }} />

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Position</InputLabel>
          <Select value={posFilter} label="Position" onChange={e => setPosFilter(e.target.value)}>
            {positions.map(p => <MenuItem key={p} value={p}>{p === 'all' ? 'All Pos' : p}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Team</InputLabel>
          <Select value={teamFilter} label="Team" onChange={e => setTeamFilter(e.target.value)}>
            <MenuItem value="">All Teams</MenuItem>
            {MLB_TEAMS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* ── Tabs ── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable" scrollButtons="auto">
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <SportsBaseball sx={{ fontSize: 15 }} />
            Tonight's Slate
            {playingToday.size > 0 && (
              <Chip label={tonight.length} size="small" sx={{ height: 17, fontSize: '0.65rem', ml: 0.5 }} />
            )}
          </Box>
        } />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><AttachMoney sx={{ fontSize: 15 }} />Value Board</Box>} />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><EmojiEvents sx={{ fontSize: 15 }} />Full Leaderboard</Box>} />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Star sx={{ fontSize: 15 }} />
            My Roster
            {roster.length > 0 && (
              <Chip label={roster.length} size="small" sx={{ height: 17, fontSize: '0.65rem', ml: 0.5 }} />
            )}
          </Box>
        } />
      </Tabs>

      {/* ── Tab info banners ── */}
      {tab === 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: alpha('#f59e0b', 0.08), border: `1px solid ${alpha('#f59e0b', 0.2)}` }}>
          <Typography variant="body2" color="text.secondary">
            {playingToday.size > 0
              ? `${tonight.length} ${view} playing tonight — ranked by FVS (rolling CVR + salary value + trend + +15 tonight bonus)`
              : 'No schedule data for today yet — check back later or browse the Value Board.'}
          </Typography>
        </Box>
      )}
      {tab === 1 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: alpha('#0891b2', 0.08), border: `1px solid ${alpha('#0891b2', 0.2)}` }}>
          <Typography variant="body2" color="text.secondary">
            Players with CVR ≥ 55 and salary ≤ $12M — ranked by Fantasy Value Score. Production at a discount.
          </Typography>
        </Box>
      )}
      {tab === 3 && myRoster.length === 0 && !loading && (
        <Box sx={{ mb: 2, p: 3, borderRadius: 2, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
          <Star sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Bookmark players with the ★ icon to build your watchlist
          </Typography>
        </Box>
      )}

      {/* ── Table ── */}
      <PlayerTable
        players={activeList}
        loading={loading}
        isPitcher={isPitcher}
        showFVS={showFVS}
        isInRoster={isInRoster}
        toggleRoster={toggleRoster}
        navigate={navigate}
        theme={theme}
      />
    </Box>
  );
};

export default FantasyDFS;
